import { create } from 'zustand';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import type { Beneficiaire } from '../types/beneficiaire';
import { toast } from 'react-hot-toast';
import { withCrmType, isCurrentCrmType } from '../utils/crmType';

interface BeneficiairesState {
  beneficiaires: Beneficiaire[];
  isLoading: boolean;
  error: string | null;
  addBeneficiaire: (beneficiaire: Omit<Beneficiaire, 'id'>) => Promise<void>;
  updateBeneficiaire: (id: string, beneficiaire: Partial<Beneficiaire>) => Promise<void>;
  deleteBeneficiaire: (id: string) => Promise<void>;
  loadBeneficiaires: () => Promise<void>;
  seedDemoBeneficiaires: () => string[];
}

export const useBeneficiairesStore = create<BeneficiairesState>((set) => ({
  beneficiaires: [],
  isLoading: false,
  error: null,

  addBeneficiaire: async (beneficiaire) => {
    if (!auth.currentUser) {
      throw new Error('Authentification requise');
    }

    try {
      const docRef = await addDoc(collection(db, 'beneficiaires'), withCrmType({
        ...beneficiaire,
        userId: auth.currentUser.uid,
        cvOk: beneficiaire.cvOk || false,
        createdAt: new Date(),
        updatedAt: new Date()
      }));
      
      set(state => ({
        beneficiaires: [...state.beneficiaires, { id: docRef.id, ...beneficiaire }],
        error: null
      }));

      toast.success('Candidat ajouté avec succès');
    } catch (error) {
      console.error('Error adding beneficiaire:', error);
      const message = 'Erreur lors de l\'ajout du candidat';
      set({ error: message });
      toast.error(message);
      throw error;
    }
  },

  updateBeneficiaire: async (id, updates) => {
    if (!auth.currentUser) {
      throw new Error('Authentification requise');
    }

    try {
      const docRef = doc(db, 'beneficiaires', id);
      const updatesWithTimestamp = {
        ...updates,
        updatedAt: new Date()
      };

      await updateDoc(docRef, updatesWithTimestamp);

      set(state => ({
        beneficiaires: state.beneficiaires.map(b => 
          b.id === id ? { ...b, ...updates } : b
        ),
        error: null
      }));

      toast.success('Candidat mis à jour avec succès');
    } catch (error) {
      console.error('Error updating beneficiaire:', error);
      const message = 'Erreur lors de la mise à jour du candidat';
      set({ error: message });
      toast.error(message);
      throw error;
    }
  },

  deleteBeneficiaire: async (id) => {
    if (!auth.currentUser) {
      throw new Error('Authentification requise');
    }

    try {
      await deleteDoc(doc(db, 'beneficiaires', id));
      set(state => ({
        beneficiaires: state.beneficiaires.filter(b => b.id !== id),
        error: null
      }));
      toast.success('Candidat supprimé avec succès');
    } catch (error) {
      console.error('Error deleting beneficiaire:', error);
      const message = 'Erreur lors de la suppression du candidat';
      set({ error: message });
      toast.error(message);
      throw error;
    }
  },

  loadBeneficiaires: async () => {
    if (!auth.currentUser) {
      set({ beneficiaires: [], error: null });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      // Load all beneficiaires without filtering by userId to allow sharing between roles
      const q = query(collection(db, 'beneficiaires'));
      const querySnapshot = await getDocs(q);
      const rawBeneficiaires = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        cvOk: doc.data().cvOk || false,
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })).filter(b => isCurrentCrmType(b)) as Beneficiaire[];

      // Deduplicate: keep only one entry per email (or per name if no email)
      const seen = new Set<string>();
      const beneficiaires = rawBeneficiaires.filter(b => {
        const key = b.email
          ? `email:${b.email.toLowerCase().trim()}`
          : `name:${(b.lastName || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim()}|${(b.firstName || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim()}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      set({ beneficiaires, isLoading: false, error: null });
    } catch (error) {
      console.error('Error loading beneficiaires:', error);
      const message = 'Erreur lors du chargement des candidats';
      set({ isLoading: false, error: message });
      toast.error(message);
      throw error;
    }
  },

  seedDemoBeneficiaires: () => {
    const now = new Date();
    const demoBeneficiaires: Beneficiaire[] = [
      {
        id: 'demo-ben-1', firstName: 'Marie', lastName: 'Dubois', title: 'Cheffe de projet digital',
        phone: '06 12 45 78 90', email: 'marie.dubois@email.fr', location: 'Lyon (69)',
        availability: 'Immédiate', experiences: [], education: [], skills: ['Agile', 'Jira', 'SAFe', 'Budget', 'Reporting'],
        languages: ['Français', 'Anglais'], desiredPosition: 'Chef de projet', currentPosition: 'Chef de projet junior',
        yearsOfExperience: 6, formation: 'Master 2 Management de projet', cvOk: true,
        createdAt: now, updatedAt: now,
      },
      {
        id: 'demo-ben-2', firstName: 'Thomas', lastName: 'Laurent', title: 'Consultant fonctionnel',
        phone: '06 23 14 87 56', email: 'thomas.laurent@email.fr', location: 'Villeurbanne (69)',
        availability: 'Sous 1 mois', experiences: [], education: [], skills: ['Agile', 'Conduite du changement', 'Jira'],
        languages: ['Français', 'Anglais', 'Espagnol'], desiredPosition: 'Chef de projet', currentPosition: 'Consultant',
        yearsOfExperience: 5, formation: 'Master 2 SI', cvOk: true,
        createdAt: now, updatedAt: now,
      },
      {
        id: 'demo-ben-3', firstName: 'Julie', lastName: 'Moreau', title: 'Project Manager',
        phone: '06 34 56 12 89', email: 'julie.moreau@email.fr', location: 'Bron (69)',
        availability: 'Immédiate', experiences: [], education: [], skills: ['PMP', 'Prince2', 'Agile', 'Budget'],
        languages: ['Français', 'Anglais'], desiredPosition: 'Chef de projet senior', currentPosition: 'Project Manager',
        yearsOfExperience: 8, formation: 'MBA Project Management', cvOk: true,
        createdAt: now, updatedAt: now,
      },
      {
        id: 'demo-ben-4', firstName: 'Nicolas', lastName: 'Petit', title: 'Comptable',
        phone: '06 45 78 23 90', email: 'nicolas.petit@email.fr', location: 'Saint-Étienne (42)',
        availability: 'Immédiate', experiences: [], education: [], skills: ['Comptabilité', 'Fiscalité', 'Sage', 'Paie'],
        languages: ['Français'], desiredPosition: 'Responsable comptable', currentPosition: 'Comptable',
        yearsOfExperience: 6, formation: 'DCG', cvOk: true,
        createdAt: now, updatedAt: now,
      },
      {
        id: 'demo-ben-5', firstName: 'Camille', lastName: 'Roux', title: 'Comptable unique',
        phone: '06 56 89 34 01', email: 'camille.roux@email.fr', location: 'Roanne (42)',
        availability: 'Sous 2 semaines', experiences: [], education: [], skills: ['Comptabilité', 'ERP', 'Paie', 'Déclarations'],
        languages: ['Français', 'Anglais'], desiredPosition: 'Responsable comptable', currentPosition: 'Comptable unique',
        yearsOfExperience: 4, formation: 'DCG', cvOk: false,
        createdAt: now, updatedAt: now,
      },
      {
        id: 'demo-ben-6', firstName: 'Alexandre', lastName: 'Fontaine', title: 'Développeur Full-Stack',
        phone: '06 67 90 45 12', email: 'alex.fontaine@email.fr', location: 'Paris (75)',
        availability: 'Immédiate', experiences: [], education: [], skills: ['React', 'TypeScript', 'Node.js', 'AWS'],
        languages: ['Français', 'Anglais'], desiredPosition: 'Développeur Full-Stack', currentPosition: 'Développeur Front',
        yearsOfExperience: 6, formation: 'Licence Pro Dev Web', cvOk: true,
        createdAt: now, updatedAt: now,
      },
      {
        id: 'demo-ben-7', firstName: 'Sarah', lastName: 'Girard', title: 'Développeuse React',
        phone: '06 78 01 56 23', email: 'sarah.girard@email.fr', location: 'Nantes (44)',
        availability: 'Sous 1 mois', experiences: [], education: [], skills: ['React', 'Node.js', 'PostgreSQL', 'Docker'],
        languages: ['Français', 'Anglais'], desiredPosition: 'Développeuse Full-Stack', currentPosition: 'Développeuse Front',
        yearsOfExperience: 4, formation: 'Master Informatique', cvOk: true,
        createdAt: now, updatedAt: now,
      },
      {
        id: 'demo-ben-8', firstName: 'Maxime', lastName: 'Chevalier', title: 'Lead Developer',
        phone: '06 89 12 67 34', email: 'maxime.chevalier@email.fr', location: 'Lyon (69)',
        availability: 'Immédiate', experiences: [], education: [], skills: ['React', 'TypeScript', 'Node.js', 'AWS', 'Docker'],
        languages: ['Français', 'Anglais'], desiredPosition: 'Tech Lead', currentPosition: 'Lead Developer',
        yearsOfExperience: 8, formation: 'Ingénieur Informatique', cvOk: true,
        createdAt: now, updatedAt: now,
      },
      {
        id: 'demo-ben-9', firstName: 'Léa', lastName: 'Brun', title: 'Développeuse Back-End',
        phone: '06 90 23 78 45', email: 'lea.brun@email.fr', location: 'Toulouse (31)',
        availability: 'Sous 2 semaines', experiences: [], education: [], skills: ['Node.js', 'PostgreSQL', 'Express', 'CI/CD'],
        languages: ['Français', 'Anglais'], desiredPosition: 'Développeuse Full-Stack', currentPosition: 'Développeuse Back',
        yearsOfExperience: 3, formation: 'BUT Informatique', cvOk: true,
        createdAt: now, updatedAt: now,
      },
    ];

    set(state => ({
      beneficiaires: [...demoBeneficiaires, ...state.beneficiaires.filter(b => !b.id.startsWith('demo-ben-'))],
      error: null,
    }));
    toast.success('9 candidats de démonstration ajoutés');
    return demoBeneficiaires.map(b => b.id);
  }
}));